import { Select } from 'antd';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import { Badge } from '@/components/Badge/Badge';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useStates from '@/hooks/pure/useStates';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { productInfoService } from '../api';
import { BaseTable, type RowData, type TableColumnT } from '../components/BaseTable';
import { ProductManagerEllipsisMenu } from '../components/DeleteItem/ProductManagerEllipsisMenu';
import { DetailModal } from '../components/DetailModal/DetailModal';
import { ListHeader } from '../components/ListHeader';
import { MergeModal } from '../components/MergeModal';
import useProductStore from '../store/ProductStore';
import type { ProductOption } from '../types';

export const OptionView = () => {
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const productMap = useProductStore((state) => state.groupedProductMap);
  const productOptions = useMemo(() => {
    return Array.from(productMap.values()).map((product) => ({
      label: product.product_name,
      value: product.alias,
    }));
  }, [productMap]);
  const optionMap = useProductStore((state) => state.productOptionMap);

  const options = useMemo(() => {
    return Array.from(optionMap.values());
  }, [optionMap]);

  const [checkedList, setCheckedList] = useState<string[]>([]);
  const mergeModal = useModalState();

  const { state, setValue } = useStates<{
    searchValue: string;
    productFilter: string[];
  }>({
    searchValue: '',
    productFilter: [],
  });

  const filteredOptionList = useMemo(() => {
    return options.filter((option) => {
      if (
        state.productFilter.length > 0 &&
        option.product_alias &&
        !state.productFilter.includes(option.product_alias)
      ) {
        return false;
      }
      if (
        state.searchValue &&
        !option.option_name
          ?.toLowerCase()
          .includes(state.searchValue.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [options, state.searchValue, state.productFilter]);

  return (
    <div className="flex flex-col gap-4 flex-1 overflow-auto">
      <ListHeader
        mergeDisabled={checkedList.length < 2}
        onSearchSubmit={(value) => {
          setValue('searchValue', value);
        }}
        clearSearch={() => {
          setValue('searchValue', '');
        }}
        onMergeClick={() => {
          mergeModal.open();
        }}
      >
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          className="min-w-40 max-w-100"
          options={productOptions}
          placeholder="Select product"
          onChange={(values) => {
            setValue('productFilter', values);
          }}
          allowClear
        />
        {checkedList.length > 0 && (
          <span className="text-sm text-gray-600">
            {checkedList.length} selected
          </span>
        )}
      </ListHeader>
      <OptionTable
        searchValue={state.searchValue}
        optionList={filteredOptionList}
        checkedList={checkedList}
        setCheckedList={setCheckedList}
      />
      <Modal status={mergeModal.status} closeModal={mergeModal.close}>
        <MergeModal
          items={checkedList.map((id) => ({
            id,
            name: optionMap.get(id)?.option_name || '-',
          }))}
          target="option"
          mergeCallback={async (targetId, list) => {
            await productInfoService.mergeProductOptions({
              from: list.filter((id) => id !== targetId),
              to: targetId,
            });
          }}
          onDoneCallback={() => {
            setCheckedList([]);
            fetchProductList(getCurrWorkspaceAlias());
          }}
        />
      </Modal>
    </div>
  );
};

const OptionTable = (props: {
  searchValue: string;
  optionList: ProductOption[];
  checkedList: string[];
  setCheckedList: Dispatch<SetStateAction<string[]>>;
}) => {
  const detailModalState = useModalState<{
    alias: string | null;
  }>({
    alias: null,
  });
  const productMap = useProductStore((state) => state.groupedProductMap);
  const { searchValue, optionList, checkedList, setCheckedList } = props;

  const OptionTableColumns: TableColumnT<ProductOption>[] = [
    {
      columnKey: 'option',
      Header: 'Option',
      Cell: (rowData: RowData<ProductOption>) => (
        <span className="truncate">{rowData.rowData.option_name}</span>
      ),
      className: 'flex-1 min-w-0',
      sortable: true,
      sort: (a, b) => {
        return a.option_name?.localeCompare(b.option_name || '') || 0;
      },
    },
    {
      columnKey: 'product',
      Header: 'Product',
      Cell: (rowData: RowData<ProductOption>) => {
        const product = productMap.get(rowData.rowData.product_alias);
        if (!product) return '-';
        return <Badge>{product.product_name}</Badge>;
      },
      className: 'w-80',
      sortable: true,
      sort: (a, b) => {
        const aProduct = productMap.get(a.product_alias);
        const bProduct = productMap.get(b.product_alias);
        return (
          aProduct?.product_name?.localeCompare(bProduct?.product_name || '') ||
          0
        );
      },
    },
    {
      columnKey: 'action',
      Header: 'Action',
      Cell: (rowData: RowData<ProductOption>) => (
        <div className="flex justify-center w-full">
          <ProductManagerEllipsisMenu
            target="option"
            targetId={rowData.rowData.id}
          />
        </div>
      ),
      className: 'w-20',
    },
  ];
  return (
    <>
      <BaseTable
        defaultSortKey="product"
        columns={OptionTableColumns}
        data={optionList.map((option) => ({
          rowKey: option.id,
          rowData: option,
        }))}
        searchValue={searchValue}
        onRowClick={(rowKey) =>
          detailModalState.open({ modalState: { alias: rowKey } })
        }
        placeholder={{
          title: 'No option yet',
        }}
        useCheckbox
        checkedList={checkedList}
        setCheckedList={setCheckedList}
      />
      <Modal
        status={detailModalState.status}
        closeModal={detailModalState.close}
      >
        {detailModalState.modalState?.alias && (
          <DetailModal
            alias={detailModalState.modalState.alias}
            type="option"
          />
        )}
      </Modal>
    </>
  );
};
